import i18n from 'i18next';
import React, {useEffect, useMemo, useState} from 'react';
import {
	ListActions,
	get,
	getStore,
	notification,
	OrganizationSelection,
	PersonSelection,
	SwList,
	SwProgressBar
} from 'simplifyworks-core-ui';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import {RegisteredDevicesList} from 'simplifyworks-push-ui';
import {TbCheck, TbX} from 'react-icons/tb';
import {FaBan} from 'react-icons/fa';
import {
	setRegDevicesUniqueCountries,
	setRegDevicesUniqueFoedusPersons,
	setRegDevicesUniqueOrganizations
} from '../../redux/actions/foedus';
import {deactivateRegisteredDevice} from '../../rest';
import {FOEDUS_ROLE} from '../../domain/constants';

const OrganizationColumnAsync = ({resource}) => {
	const [name, setName] = useState(null);
	const foedusReduxState = getStore().getState()?.foedus;

	useEffect(() => {
		const foundOrganization = foedusReduxState?.regDevicesUniqueOrganizations?.find(organization => organization?.userId === resource?.user?.id);

		if (!foundOrganization) {
			get('/api/foedus/contacts/registered-devices/user-organization/' + resource?.user?.id,
				(result) => {
					setName(result?.organization?.name);

					getStore().dispatch(
						setRegDevicesUniqueOrganizations({
							regDevicesUniqueOrganizations: [
								...(foedusReduxState?.regDevicesUniqueOrganizations ?? []),
								{
									...result?.organization,
									userId: resource?.user?.id
								}
							]
						})
					);
				}
			);
		} else {
			setName(foundOrganization?.name);
		}
	}, []);

	return useMemo(() => {
		if (name == null) {
			return <SwProgressBar centered size='sm'/>;
		} else {
			return name;
		}
	}, [name]);
};

const CountryColumnAsync = ({resource}) => {
	const [name, setName] = useState(null);
	const [code, setCode] = useState(null);
	const foedusReduxState = getStore().getState()?.foedus;

	useEffect(() => {
		const foundCountry = foedusReduxState?.regDevicesUniqueCountries?.find(country => country?.userId === resource?.user?.id);
		if (!foundCountry) {
			get('/api/foedus/contacts/registered-devices/user-organization/' + resource?.user?.id,
				(result) => {
					setName(result?.organization?.organization?.name);
					setCode(result?.organization?.organization?.code);

					getStore().dispatch(
						setRegDevicesUniqueCountries({
							regDevicesUniqueCountries: [
								...(foedusReduxState?.regDevicesUniqueCountries ?? []),
								{
									...result?.organization?.organization,
									userId: resource?.user?.id
								}
							]
						})
					);
				}
			);
		} else {
			setName(foundCountry?.name);
			setCode(foundCountry?.code);
		}
	}, []);

	return useMemo(() => {
		if (name == null) {
			return <SwProgressBar centered size='sm'/>;
		} else {
			return <div className="d-flex align-items-center">
				<img style={{width: '24px'}} src={'https://flagsapi.com/' + code + '/shiny/24.png'}/>
				&nbsp;
				{name}
			</div>;
		}
	}, [name, code]);
};

const PersonColumnAsync = ({resource}) => {
	const [link, setLink] = useState(null);
	const foedusReduxState = getStore().getState()?.foedus;

	useEffect(() => {
		const foundFoedusPerson = foedusReduxState?.regDevicesUniqueFoeudusPersons?.find(foedusPerson => foedusPerson?.person?.id === resource?.id);
		if (!foundFoedusPerson) {
			get('/api/foedus/contacts/registered-devices/foedus-person/' + resource?.id,
				(result) => {
					setLink('/simplifyworks/contacts/people/' + result.id + '/base-info');

					getStore().dispatch(
						setRegDevicesUniqueFoedusPersons({
							regDevicesUniqueFoeudusPersons: [
								...(foedusReduxState?.regDevicesUniqueFoeudusPersons ?? []),
								result
							]
						})
					);
				}
			);
		}
	}, []);

	return useMemo(() => {
		if (link == null) {
			return PersonSelection.renderExtendedText(resource);
		} else {
			return (
				<a href={link || ''}>
					{PersonSelection.renderExtendedText(resource)}
				</a>
			);
		}
	}, [link]);
};

const RegDevicesList = (props) => {
	const countryFixedFilter = useMemo(() => {
		return {
			organizationTypes: props?.countryCoreCodebook ? [props?.countryCoreCodebook] : []
		};
	}, [props?.countryCoreCodebook]);

	// Columns specific for Foedus app
	const columns = [
		{
			property: 'user.person',
			labelKey: 'core-person',
			filterable: true,
			component: SwList.Entity,
			render: (resource, f) => <PersonColumnAsync resource={resource}/>,
			renderHeadFilter: (resource) => resource?.niceLabelExtended,
			children: PersonSelection.renderSelection(),
			index: 2
		},
		{
			property: 'organization',
			labelKey: 'core-organization',
			filterable: false,
			orderable: false,
			component: SwList.Custom,
			render: (resource) => <OrganizationColumnAsync resource={resource}/>,
			index: 3
		},
		{
			property: 'country',
			labelKey: 'foedus-country',
			filterable: false,
			orderable: false,
			component: SwList.Custom,
			render: (resource) => <CountryColumnAsync resource={resource}/>,
			children: OrganizationSelection.renderSelection({
				name: 'foedus-person-organization',
				fixedFilter: countryFixedFilter
			}),
			index: 4
		},
		// Overrides for default columns
		{
			property: 'created',
			labelKey: 'push-registration-date',
			component: SwList.Datetime,
			userPattern: 'D.M.YYYY, HH:ss',
			index: 5
		},
		{
			property: 'disabled',
			labelKey: 'foedus-contacts-registered-device-status',
			filterable: true,
			width: '100px',
			component: SwList.Custom,
			render: (resource) => (
				<div className="d-flex align-items-center justify-content-center w-100 h-100">
					{resource?.disabled ? <TbX size={24} color={'#EC3D3D'}/> : <TbCheck size={24} color={'#28a745'}/>}
				</div>
			),
			index: 6
		}
	];

	if (props?.loggedUserPrimaryFoedusRole === FOEDUS_ROLE.ADMIN) {
		columns.push(
			{
				property: 'actions',
				labelKey: ' ',
				width: '124px',
				filterable: false,
				orderable: false,
				component: SwList.Custom,
				render: (resource) => {
					if (props?.loggedUserPrimaryFoedusRole !== FOEDUS_ROLE.ADMIN) {
						return null;
					}

					const tooltip = (
						<Tooltip id={'tooltip-deactivate-' + resource?.deviceId}>
							{i18n.t(resource?.disabled
								? 'foedus-contacts-registered-devices-disable-disabled-tooltip'
								: 'foedus-contacts-registered-devices-disable-tooltip'
							)}
						</Tooltip>
					);

					return (
						<OverlayTrigger
							placement="top"
							overlay={tooltip}
						>
							<span>
								<Button
									variant='danger'
									disabled={resource?.disabled}
									onClick={() => {
										deactivateRegisteredDevice(
											{
												os: resource?.os,
												deviceId: resource?.deviceId
											},
											() => {
												notification.showLocalizedMessage({
													level: 'success',
													titleKey: 'foedus-contacts-registered-devices-disable-success-title',
													messageKey: 'foedus-contacts-registered-devices-disable-success-text'
												});
												props?.actions?.list?.reload('push-registered-devices-list');
											},
											() => {
												notification.showLocalizedMessage({
													level: 'error',
													titleKey: 'foedus-contacts-registered-devices-disable-error-title',
													messageKey: 'foedus-contacts-registered-devices-disable-error-text'
												});
											}
										);
									}}
								>
									<FaBan size={12}/>
										&nbsp; {i18n.t('foedus-contacts-registered-devices-disable')}
								</Button>
							</span>
						</OverlayTrigger>
					);
				},
				index: 7
			}
		);
	}

	return <>
		<RegisteredDevicesList columns={columns} />

	</>;
};

const mapStateToProps = (state) => {

	return {
		nationalBodyCoreCodebook: state?.foedus?.nationalBodyCoreCodebook,
		countryCoreCodebook: state?.foedus?.countryCoreCodebook,
		loggedUserPrimaryFoedusRole: state?.foedus?.loggedUserPrimaryFoedusRole
	};
};

const mapDispatchToProps = (dispatch) => ({
	actions: {
		list: bindActionCreators(ListActions, dispatch)
	}
});

export default {
	title: () => i18n.t('foedus-registered-devices-title-list'),
	main: connect(mapStateToProps, mapDispatchToProps)(RegDevicesList)
};
